import { ArrowLeftIcon } from 'fbm-icons';
import {
  Box,
  Button,
  IconButton,
  Image,
  Link,
  Popover,
  Typography,
} from 'fbm-ui';
import { Stack } from 'fbm-ui/lib/mui';
import Layout from './Layout';
import SimulateAPI from '../../api/simulate';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showErrorMessage } from '../../utils/apiCheck';
import { Provider as ReduxProvider } from 'react-redux';
import store from '../../redux/store';
import { useTranslation } from 'react-i18next';
import { LinearProgress } from '@avocadoui/components';
import { IS_JIUYEQIAO } from '../../oem';

// status: 0, 未开始; 1, 进行中; 2, AI 生成中; 3, 已完成
// 如果status > 1 则显示报告列表 2
// 如果 流程中所有status > 1 并且 status ==== 3 并且isend === true 则显示再练一次
const MAX_TIME = 10 * 60;

function timestampToDateAndTime(timestamp) {
  if (timestamp === '' || timestamp === 'NaN') {
    return '';
  }
  const now = new Date(parseInt(timestamp) * 1000);
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  return `${y}-${m < 10 ? `0${m}` : m}-${d < 10 ? `0${d}` : d} ${now
    .toTimeString()
    .substr(0, 5)}`;
}

function some(arr, testFunc) {
  for (let i = 0; i < arr.length; i++) {
    if (testFunc(arr[i])) {
      return true;
    }
  }
  return false;
}

function buttonType(isEnd, hasAIInterview, isAiInterveiwfinished) {
  if (isEnd) {
    if (!hasAIInterview) return 3;
    if (!isAiInterveiwfinished) return 1;
    if (hasAIInterview && isAiInterveiwfinished) return 3;
  }
  return 2;
}

function progress(item) {
  // const token = item.applicationProcessToken;
  // let hash = 0;
  // for (let i = 0; i < token.length; i++) {
  //   const char = token.charCodeAt(i);
  //   hash = (hash << 5) - hash + char;
  //   hash = hash & hash;
  // }
  // const normalizedHash = Math.abs(hash) % 100;
  // return normalizedHash;

  const time = +new Date() / 1000 - (item?.completeTime ?? 0);
  // return time / MAX_TIME > 99 ? 99 : (time / MAX_TIME) * 100;
  const result = (time * 100) / MAX_TIME;
  if (result > 99) return 99;
  return result;
}

const Simulate = () => {
  const loading = useRef(false);
  const { t } = useTranslation();
  const { type, token } = useParams();
  const navigate = useNavigate();
  const [, setCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCount((prevCount) => prevCount + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  function title(item) {
    if (item.status === 2) return t('Processing (Approx. 10 mins)');
    if (
      some(item.steps, function (v) {
        return v.scoreInfo.abilities.length > 0;
      })
    )
      return item.aiScore + t('Score');
    return '-';
  }
  function time(item) {
    if (item.status !== 3) return `${t('Report Time')}：-`;
    return `${t('Report Time')}：` + item.completeTimeFormat;
  }

  function formatDuration(item) {
    const du = item.duration;
    const status = item.status;

    if (du === 0 || status !== 3) return '-';

    // 自定义格式化分钟和秒
    const minutes = Math.floor(du / 60);
    const seconds = du % 60;
    if (minutes > 0) {
      return t('mmmsss', { mm: minutes, ss: seconds });
    } else {
      return t('sss', { ss: seconds });
    }
  }

  const [processInfo, setProcessInfo] = useState({
    positionName: '',
    positionToken: '',
    postTime: '',
    enterpriseName: '',
    isEnd: false,
    hasAIInterview: false,
    isAiInterveiwfinished: false,
    list: [],
  });

  const { positionToken } = processInfo;

  const handleContinue = useCallback(() => {
    navigate(`/login/${positionToken}`);
  }, [navigate, positionToken]);

  const handleRetry = useCallback(() => {
    if (loading.current) return;
    loading.current = true;
    SimulateAPI.simulateRetry(token)
      .then((res) => {
        console.log(res);
        navigate(`/login/${positionToken}`);
      })
      .catch(showErrorMessage)
      .finally(() => {
        loading.current = false;
      });
  }, [navigate, positionToken, token]);

  useEffect(() => {
    SimulateAPI.simulateApplicationProcess(token)
      .then((res) => {
        const { list } = res;
        const filteredProcess = list?.filter((v) => v.status > 1) || [];
        setProcessInfo({
          ...res,
          list: filteredProcess,
          isAiInterveiwfinished:
            list
              .filter((v) => !v.isHistory)
              .filter((v) => [1, 3].includes(v.status)).length ===
            list.filter((v) => !v.isHistory).length,
        });
      })
      .catch(showErrorMessage);
  }, [token]);

  const handleBack = () => {
    navigate('/simulate/record');
  };

  const handleReport = (applicationProcessToken) => {
    navigate(`/report/${type}/${applicationProcessToken}`);
  };

  const actionType = buttonType(
    processInfo.isEnd,
    processInfo.hasAIInterview,
    processInfo.isAiInterveiwfinished
  );

  return (
    <ReduxProvider store={store}>
      <Layout>
        <Stack
          sx={{
            width: 'min(720px, calc(100vw - 32px))',
            pb: 2,
          }}
        >
          <Box mb={2}>
            <IconButton onClick={handleBack}>
              <ArrowLeftIcon sx={{ color: 'rgba(0,0,0,0.86)' }} />
            </IconButton>
          </Box>
          <Stack
            spacing={1}
            sx={{
              p: 3,
              pr: actionType === 2 || actionType === 3 ? '150px' : 3,
              height: 102,
              position: 'relative',
              borderRadius: '16px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
              backgroundImage:
                'linear-gradient(166deg, #C6E9F7 0%, #C2D5F2 100%)',
            }}
          >
            <Typography variant="h6" fontWeight="medium">
              {processInfo.positionName}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {t('Start Tim')}：{timestampToDateAndTime(processInfo.postTime)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {processInfo.enterpriseName}
              </Typography>
            </Stack>
            {(actionType === 2 || actionType === 3) && (
              <Button
                onClick={actionType === 3 ? handleRetry : handleContinue}
                sx={{
                  position: 'absolute',
                  right: '30px',
                  top: 'calc(50% - 3px)',
                  transform: 'translateY(-50%)',
                }}
              >
                {actionType === 3 ? t('Once More') : t('Continue')}
              </Button>
            )}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            columnGap={2}
            rowGap={1}
          >
            <Typography variant="h6" fontWeight="medium" mt={4} mb={2} mr={2}>
              {t('AI Mock Interview Report')}
            </Typography>
            <Typography
              variant="body2"
              color="error"
              mt={4}
              mb={2}
              hidden={!IS_JIUYEQIAO}
            >
              面试报告生成大约需要10分钟，可以微信扫码在
              <Popover
                placement="bottom"
                content={
                  <Box>
                    <Image
                      src="https://webcdn.fbmms.cn/assets/xKEy/PkB9ORTWxwg84tLpJs_T8.jpeg"
                      width={200}
                    />
                  </Box>
                }
              >
                <Link
                  href="#"
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    // 设置点击颜色
                    color: 'rgb(77, 134, 222)',
                  }}
                >
                  智面星小程序
                </Link>
              </Popover>
              查看报告。
            </Typography>
          </Stack>
          {(!processInfo?.list || processInfo?.list?.length === 0) && (
            <Typography variant="body2" color="textSecondary">
              {t(
                'No Report Available, AI Mock Interview has not been completed yet.'
              )}
            </Typography>
          )}
          <Box
            sx={{
              minHeight: 400,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              alignItems: 'start',
              gap: 3,
              width: '100%',
              padding: 0,
            }}
          >
            {processInfo.list.map((item) => (
              <Stack
                key={item.applicationProcessToken}
                sx={{
                  p: 3,
                  pb: 2,
                  borderRadius: '16px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.16)',
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  minHeight: 186,
                  position: 'relative',
                }}
              >
                {item.completeStatus === 2 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 74,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      right: 0,
                      top: 0,
                      borderRadius: '16px 16px 0px 16px',
                      backgroundColor: 'rgba(245, 196, 65, 0.15)',
                      color: 'rgba(236, 175, 11, 1)',
                      fontSize: 14,
                      fontWeight: 'medium',
                    }}
                  >
                    未答完
                  </Box>
                )}
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  mb={3}
                  color="rgb(76, 175, 80)"
                >
                  {title(item)}
                </Typography>
                <Typography variant="body2" mb={2}>
                  {time(item)}
                </Typography>
                <Typography variant="body2">
                  {t('Interview Duration')}：{formatDuration(item)}
                </Typography>
                <Box display="flex" justifyContent="flex-end">
                  {item.status === 3 && (
                    <Button
                      color="inherit"
                      variant="outlined"
                      sx={{ fontWeight: 'medium' }}
                      onClick={() => handleReport(item.applicationProcessToken)}
                    >
                      {t('View Report')}
                    </Button>
                  )}
                </Box>
                {item.status === 2 && IS_JIUYEQIAO && item.completeStatus > 2 && (
                  <Stack direction="row" alignItems="center" spacing={1} py={2}>
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      生成进度:
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={progress(item)}
                      sx={{ width: 1 }}
                    />
                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                      {progress(item).toFixed(2)} %
                    </Typography>
                  </Stack>
                )}
              </Stack>
            ))}
          </Box>
        </Stack>
      </Layout>
    </ReduxProvider>
  );
};

export default Simulate;
